import { useState, useEffect, useContext, useMemo } from 'react'
import { useLocation, useParams, useNavigate } from 'react-router-dom'

import { Typography } from '@mui/material'

import moment from 'moment-timezone'

import PageWrapper from '/components/page-wrapper'
import { AppContext } from '/providers/app-context'
import HelpText from '/help/pass-details'
import Menu from '/components/passes/pass-nav-menu'
import StyledPaper from '/components/paper'

import Preview from '/components/passes/preview'
import Summary from '/components/passes/summary'

import { getPass } from '/providers/passes-api'

const PassDetails = () => {

  const navigate = useNavigate()
  const location = useLocation()

  const { templateId, passId } = useParams()
  const { apiSource, apiKey, templates, passes } = useContext(AppContext)

  const [pass, setPass] = useState(location.state?.pass)

  useEffect(() => {
    getPassData()
  }, [])

  const template = useMemo(() => { return templates.filter(t => t.id === templateId)[0] }, [templates, templateId])

  useEffect(() => {
    const newPass = passes.filter(p => p.template_id === templateId && p.pass_id === passId)[0]
    if (newPass) setPass(newPass)
  }, [passes])

  const getPassData = async () => {
    const p = await getPass({ apiSource, apiKey, templateId, passId })
    if (p.ok === false) navigate("/templates")
    setPass(p.pass)
  }

  return (
    <PageWrapper title="Pass Details" HelpText={HelpText} heading={`${pass?.pass_id} ${pass?.mode === "test" ? " [test]" : ""}`} menu={<Menu pass={pass} template={template} />}>
      <Content template={template} pass={pass} setPass={setPass} />
    </PageWrapper>
  )
}

export default PassDetails

const Content = ({ template, pass, setPass }) => {
  if (template && pass) return (
    <div className='flex flex-col w-full gap-5 overflow-y-scroll h-full max-w-full'>

      <div className='flex flex-wrap w-full gap-5'>
        <div>
          <Summary template={template} pass={pass} setPass={setPass} classes="flex-grow flex-shrink mb-5 max-w-[600px]" />
          <SmartixScannerPassInfo template={template} pass={pass} />
        </div>
        <Preview template={template} pass={pass} classes="" />
      </div>
    </div>
  )
}

const SmartixScannerPassInfo = ({ template, pass }) => {

  if (!template.smartix_scanner) return null

  switch (template.pass_type_id) {
    case "smartix_stamp": return <SmartixStamp pass={pass} />
    case "smartix_gift": return <SmartixGift pass={pass} />
    case "smartix_loyalty": return <SmartixLoyalty pass={pass} />
    case "smartix_event": return <SmartixEvent template={template} pass={pass} />
    case "smartix_event_bg": return <SmartixEvent pass={pass} />
  }
}

const SmartixStamp = ({ pass }) => {

  const lastCollection = pass.scanner_audit_data.filter(i => i.action === "collect")[0]
  const lastRedemption = pass.scanner_audit_data.filter(i => i.action === "redeem")[0]

  return (
    <StyledPaper>
    <div className='flex flex-col gap-2'>
      <Typography variant='h3'>Stampcard details</Typography>

      <div className='flex flex-row justify-between'><div>Issued</div><div>{formatDate(pass.creation_timestamp)}</div></div>
      <div className='flex flex-row justify-between'><div>Status</div><div>{pass.scanner_instance_data["*_disabled"] === null ? "Enabled" : "Disabled (" + formatDateTime(pass.scanner_instance_data["*_disabled"]) + ")"}</div></div>

      <div className='flex flex-row justify-between'><div>Current Stamps</div><div>{pass.scanner_instance_data["__*_current_stamps__"]}</div></div>
      <div className='flex flex-row justify-between'><div>Current Full Cards</div><div>{pass.scanner_instance_data["__*_current_full_cards__"]}</div></div>

      <div className='flex flex-row justify-between'><div>Full Cards Redeemed</div><div>{pass.scanner_instance_data["__*_total_redemptions__"]}</div></div>

      <div className='flex flex-row justify-between'><div>Total Stamps Collected</div><div>{pass.scanner_instance_data["__*_total_stamps__"]}</div></div>

      <div className='flex flex-row justify-between'><div>Customer Name</div><div>{pass.scanner_instance_data.__customer_name__}</div></div>
      <div className='flex flex-row justify-between'><div>Security Question</div><div>{pass.scanner_instance_data.__security_question__}</div></div>
      <div className='flex flex-row justify-between'><div>Answer</div><div>{pass.scanner_instance_data.__security_answer__}</div></div>

      <div className='flex flex-row justify-between'><div>Last Collection</div><div>{lastCollection ? `${formatDate(lastCollection.timestamp)} : ${lastCollection.stamps} stamps` : "-"}</div></div>
      <div className='flex flex-row justify-between'><div>Last Redemption</div><div>{lastRedemption ? `${formatDate(lastRedemption.timestamp)} : ${lastRedemption.cards} cards` : "-"}</div></div>

      <div className='flex flex-row justify-between'><div>Note</div><div>{pass.scanner_instance_data["*_note"]}</div></div>
    </div>
    </StyledPaper>
  )
}

const SmartixGift = ({ pass }) => {

  const now = moment().format("YYYYMMDDHHmmSS")
  const lastBalanceChange = pass.scanner_audit_data.filter(i => ["debit", "refund", "credit"].includes(i.action)).pop()
  const lastUsedDate = lastBalanceChange?.timestamp

  return (
    <div className='flex flex-col gap-2 ml-20 mr-20 pt-10 pb-20'>
      <Typography variant='h3'>Giftcard details</Typography>

      <div className='flex flex-row justify-between'><div>Issued</div><div>{formatDate(pass.creation_timestamp)}</div></div>
      <div className='flex flex-row justify-between'><div>Gift card amount</div><div>£{pass.scanner_instance_data["__*_amount__"]?.toFixed(2)}</div></div>
      <div className='flex flex-row justify-between'><div>Gift card balance</div><div>£{pass.scanner_instance_data["__*_balance__"]?.toFixed(2)}</div></div>
      <div className='flex flex-row justify-between'><div>Status</div><div>{pass.scanner_instance_data["*_disabled"] === null ? "Enabled" : `Disabled ${formatDate(pass.scanner_instance_data["*_disabled"])}`}</div></div>
      <div className='flex flex-row justify-between'><div>Expires</div><div>{pass.expiry_date < now ? formatDate(pass.expiry_date) : `Giftcard expired ${formatDate(pass.expiry_date)}`}</div></div>

      <div className='flex flex-row justify-between'><div>Last used</div><div>{lastUsedDate ? formatDate(lastUsedDate) : "Not used"}</div></div>
      <div className='flex flex-row justify-between'><div>Note</div><div>{pass.scanner_instance_data["*_note"]}</div></div>

    </div>
  )
}

const SmartixLoyalty = ({ pass }) => {

  const now = moment().format("YYYYMMDDHHmmSS")
  const lastBalanceChange = pass.scanner_audit_data.filter(i => ["debit", "refund", "credit"].includes(i.action)).pop()
  const lastUsedDate = lastBalanceChange?.timestamp

  return (
    <div className='flex flex-col gap-2 ml-20 mr-20 pt-10 pb-20'>
      <Typography variant='h3'>Giftcard details</Typography>

      <div className='flex flex-row justify-between'><div>Issued</div><div>{formatDate(pass.creation_timestamp)}</div></div>
      <div className='flex flex-row justify-between'><div>Points balance</div><div>{pass.scanner_instance_data["__*_points__"]}</div></div>
      <div className='flex flex-row justify-between'><div>Status</div><div>{pass.scanner_instance_data["*_disabled"] === null ? "Enabled" : `Disabled ${formatDate(pass.scanner_instance_data["*_disabled"])}`}</div></div>
      <div className='flex flex-row justify-between'><div>Expires</div><div>{pass.expiry_date ? pass.expiry_date < now ? formatDate(pass.expiry_date) : `Giftcard expired ${formatDate(pass.expiry_date)}` : '-'}</div></div>

      <div className='flex flex-row justify-between'><div>Last used</div><div>{lastUsedDate ? formatDate(lastUsedDate) : "Not used"}</div></div>
      <div className='flex flex-row justify-between'><div>Note</div><div>{pass.scanner_instance_data["*_note"]}</div></div>

    </div>
  )
}

const SmartixEvent = ({ template, pass }) => {
  return (
    <div className='flex flex-col gap-2 ml-20 mr-20 pt-10 pb-20'>
      <Typography variant='h3'>Event Ticket details</Typography>

      <div className='flex flex-row justify-between'><div>Issued</div><div>{formatDate(pass.creation_timestamp)}</div></div>
      <div className='flex flex-row justify-between'><div>Used</div><div>{pass.scanner_instance_data["*_entry_count"] > 0 ? "Yes (" + formatDateTime(pass.scanner_instance_data["*_first_entry_timestamp"]) + ")" : "No"}</div></div>
      <div className='flex flex-row justify-between'><div>Status</div><div>{pass.scanner_instance_data["*_disabled"] === null ? "Enabled" : "Disabled (" + formatDateTime(pass.scanner_instance_data["*_disabled"]) + ")"}</div></div>

      {template.scanner_data.reentry_permitted &&
        <>
          <div className='flex flex-row justify-between'><div>Entry Count</div><div>{pass.scanner_instance_data["*_entry_count"]}</div></div>
          <div className='flex flex-row justify-between'><div>Exit Count</div><div>{pass.scanner_instance_data["*_exit_count"]}</div></div>

          <div className='flex flex-row justify-between'><div>First entry</div><div>{pass.scanner_instance_data["*_first_entry_timestamp"] === null ? "-" : formatDateTime(pass.scanner_instance_data["*_first_entry_timestamp"])}</div></div>
          <div className='flex flex-row justify-between'><div>Last entry</div><div>{pass.scanner_instance_data["*_last_entry_timestamp"] === null ? "-" : formatDateTime(pass.scanner_instance_data["*_last_entry_timestamp"])}</div></div>
        </>
      }

      <div className='flex flex-row justify-between'><div>Note</div><div>{pass.scanner_instance_data["*_note"]}</div></div>
    </div>
  )
}


const formatDate = date => moment(date, "YYYYMMDDHHmmSS").format("DD MMM YYYY")
const formatDateTime = date => moment(date, "YYYYMMDDHHmmSS").format("DD MMM YYYY HH:mm")